import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import { TabList, Tab } from "reakit/Tab";

import PageTitle from "../Pages/PageTitle";
import { UserAvatarWithoutRelay } from "../Users/UserAvatar";
import { useFormatMessage } from "../Intl/Context";
import { useProfileLayout } from "../Routes/ProfileLayoutRoute";
import KEY_CODES from "../Utils/keyCodes";
import { useDeferredEffect } from "../Utils/useDeferredEffect";

const ProfileHeader = () => {
  const user = useSelector(({ viewer }) => viewer);

  const t = useFormatMessage();
  const titleLabel = t(intlMessages.title);

  const { tabState, tabsData } = useProfileLayout();

  const [currentTabPanelTargetId, setCurrentTabPanelTargetId] = useState();

  // When a tab is selected, register its tab panel id into the currentTabPanelTargetId
  // state variable, so it can be automatically focused when the page is rendered
  const handleClick = useCallback(({ currentTarget: linkNode }) => {
    const targetId = linkNode.getAttribute("aria-controls");

    setCurrentTabPanelTargetId(targetId);
  }, []);

  // This ensures that, when a tab is selected and its tab panel id stored in the current
  // component's state, we force the browser to focus the related tab panel.
  useDeferredEffect(() => {
    const targetTabPanel = document.getElementById(currentTabPanelTargetId);
    if (targetTabPanel) return targetTabPanel.focus();

    // Just like in the `useRefocusOnNextRender` hook, this setTimeout call is used to
    // allow the page to be loaded if the target page wasn't in the relay cache, but
    // doesn't force the focus if it was moved between the tab selection and the page
    // loading.
    setTimeout(() => {
      const { activeElement } = document;

      if (
        activeElement !== document.body &&
        activeElement.getAttribute("role") !== "tab"
      ) {
        return;
      }

      document.getElementById(currentTabPanelTargetId)?.focus();
    }, 1000);
  }, [currentTabPanelTargetId]);

  const handleKeyDown = useCallback(e => {
    if (e.keyCode !== KEY_CODES.SPACE) return;

    e.preventDefault();
    e.currentTarget.click();
  }, []);

  return (
    <>
      <PageTitle
        className="margin-y-xs"
        titleContainerClassName="d-flex align-items-center"
        page={user}
        title={titleLabel}
        shareButtons={false}
        subscribable={false}
        breadcrumbs={false}
        childrenPosition="before"
      >
        <UserAvatarWithoutRelay user={user} className="margin-right-sm" />
      </PageTitle>

      <div className="box margin-bottom-xs padding-sm d-flex justify-content-center">
        <TabList
          {...tabState}
          as="ul"
          role="tablist"
          className="profile-submenu-links list-unstyled d-inline-flex flex-row m-auto text-subtitle-page"
        >
          {tabsData.map(({ identifier, href, tabId, className, label, isActive }) => (
            <li
              key={identifier}
              role="presentation"
              className="profile-link-item profile-link-main"
            >
              <Tab
                {...tabState}
                as={"a"}
                href={href}
                className={className}
                role="tab"
                id={tabId}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                aria-selected={isActive}
              >
                {label}
              </Tab>
            </li>
          ))}
        </TabList>
      </div>
    </>
  );
};

export default ProfileHeader;

const intlMessages = defineMessages({
  title: {
    id: "profile.header.title",
    defaultMessage: "Mon profil"
  }
});
